import { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { User, Users, Gift, Settings, Search, Bell, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/common";
import { SearchOverlay } from "@/components/SearchOverlay";
import { NotificationPanel } from "@/components/NotificationPanel";
import { InstallPrompt } from "@/components/InstallPrompt";
import { MailWarning, X as XIcon } from "lucide-react";
import { toast } from "sonner";

function VerifyBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  if (!user || user.email_verified || dismissed) return null;
  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox (and Spam/Junk folder).");
    } catch {
      toast.error("Couldn't send right now. Try again shortly.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="iks-card border-amber-500/30 bg-amber-500/5 p-3.5 mb-4 flex items-center gap-3" data-testid="verify-banner">
      <MailWarning className="text-amber-400 shrink-0" size={18} />
      <p className="text-sm text-amber-100/90 flex-1">Verify your email to secure your account.</p>
      <button onClick={resend} disabled={sending} data-testid="resend-verify-btn" className="text-sm font-medium text-amber-300 hover:text-amber-200 px-2 py-1 rounded-lg hover:bg-white/5">
        {sending ? "Sending…" : "Resend"}
      </button>
      <button onClick={() => setDismissed(true)} className="text-amber-400/60 hover:text-amber-300 p-1"><XIcon size={16} /></button>
    </div>
  );
}

const NAV = [
  { to: "/profile", label: "Profile", icon: User },
  { to: "/connections", label: "Connections", icon: Users },
  { to: "/wishlist", label: "WishList", icon: Gift },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function AppShell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread);
    } catch {}
  }, []);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, [loadUnread, loc.pathname]);

  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");

  return (
    <div className="min-h-screen bg-[#070A12] text-slate-100">
      {/* Top header */}
      <header className="fixed top-0 inset-x-0 z-40 iks-glass border-b border-white/10 safe-top">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/profile" data-testid="header-brand"><Brand size={30} /></Link>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="open-search-btn"
              className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-300 hover:bg-white/5 transition-colors"
            >
              <Search size={20} />
            </button>
            <button
              onClick={() => setNotifOpen(true)}
              data-testid="open-notifications-btn"
              className="relative w-10 h-10 rounded-xl flex items-center justify-center text-slate-300 hover:bg-white/5 transition-colors"
            >
              <Bell size={20} />
              {unread > 0 && (
                <span
                  data-testid="notif-badge"
                  className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-sky-400 text-[10px] font-bold text-[#070A12] flex items-center justify-center"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed left-0 top-14 bottom-0 w-56 flex-col border-r border-white/10 bg-[#0B0F19]/60 p-3 gap-1 z-30">
        {NAV.map((n) => {
          const Icon = n.icon;
          const active = isActive(n.to);
          return (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`sidebar-${n.label.toLowerCase()}`}
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                active ? "bg-blue-600/15 text-sky-300" : "text-slate-400 hover:text-slate-100 hover:bg-white/5"
              }`}
            >
              <Icon size={19} /> {n.label}
            </Link>
          );
        })}
        {user?.role === "admin" && (
          <Link
            to="/admin"
            data-testid="sidebar-admin"
            className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-amber-300/80 hover:bg-white/5 mt-auto"
          >
            <Shield size={19} /> Admin
          </Link>
        )}
      </aside>

      {/* Main content */}
      <main className="md:pl-56 md:pb-10 min-h-screen" style={{ paddingTop: "calc(3.5rem + env(safe-area-inset-top))", paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5">
          <VerifyBanner />
          <Outlet context={{ reloadUnread: loadUnread }} />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 iks-glass border-t border-white/10 safe-bottom">
        <div className="flex items-center justify-around h-16">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = isActive(n.to);
            return (
              <button
                key={n.to}
                onClick={() => nav(n.to)}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`flex flex-col items-center gap-1 px-3 py-1.5 transition-colors ${
                  active ? "text-sky-400" : "text-slate-500"
                }`}
              >
                <Icon size={22} />
                <span className="text-[10px] font-medium">{n.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <NotificationPanel
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        onChange={loadUnread}
      />
      <InstallPrompt />
    </div>
  );
}
